import React, { useEffect } from 'react';
import { Modal, Upload, message } from 'antd';
import { ArrowLeftOutlined, CloudUploadOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Typography,
  Button
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import services from '../../services';
import axios from 'axios';

const muiTheme = createTheme({
  zIndex: {
    modal: 3500, // Ensure dropdowns appear above Ant Design modal (z-index 2000)
    snackbar: 3600,
    tooltip: 3700
  },
  typography: {
    fontFamily: 'Manrope, sans-serif',
    allVariants: {
      fontFamily: 'Manrope, sans-serif',
    },
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
          '&.Mui-disabled': {
            backgroundColor: '#FFFFFF', // Keep white background
            color: '#667085', // Readable gray
            WebkitTextFillColor: '#667085',
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Manrope, sans-serif',
          '&.Mui-focused': {
            color: '#5FB92A', // Green when focused
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#5FB92A', // Green when focused
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#D0D5DD',
        },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#5FB92A', // Green on hover
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#5FB92A', // Green when focused
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D0D5DD', // Keep border visible
          }
        }
      }
    }
  }
});

const DRAFT_KEY = 'challenge_create_draft';

const CreateChallengeModal = ({ open, onClose, mode = 'create', initialData = null, onEdit, onSubmit }) => {
  const [formData, setFormData] = React.useState({
    name: "",
    type: "Monthly",
    mode: "Bicycle",
    keyRequirements: "",
    description: "",
    co2ToSave: "",
    frequency: 1,
    repetition: "One-time",
    status: "Active",
    challengeImageUrl: "",
    rewardBadgeImageUrl: ""
  });

  const [challengeImageFile, setChallengeImageFile] = React.useState(null);
  const [badgeImageFile, setBadgeImageFile] = React.useState(null);
  const [challengeImagePreview, setChallengeImagePreview] = React.useState("");
  const [badgeImagePreview, setBadgeImagePreview] = React.useState("");
  const [uploadingChallenge, setUploadingChallenge] = React.useState(false);
  const [uploadingBadge, setUploadingBadge] = React.useState(false);

  const [effectiveFrom, setEffectiveFrom] = React.useState(null);
  const [effectiveTo, setEffectiveTo] = React.useState(null);
  const [effectiveFromOpen, setEffectiveFromOpen] = React.useState(false);
  const [effectiveToOpen, setEffectiveToOpen] = React.useState(false);
  const [hasDraft, setHasDraft] = React.useState(false);

  // Validation state
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});

  // Reset or populate form whenever the modal opens
  useEffect(() => {
    if (!open) return;

    if (initialData && (mode === 'view' || mode === 'edit')) {
      const convertedMode = initialData.mode ? (initialData.mode.charAt(0).toUpperCase() + initialData.mode.slice(1).toLowerCase()) : "Bicycle";

      setFormData({
        name: initialData.name || "",
        type: initialData.type ? (initialData.type.charAt(0).toUpperCase() + initialData.type.slice(1).toLowerCase()) : "Monthly",
        mode: convertedMode,
        keyRequirements: initialData.keyRequirements || "",
        description: initialData.description || "",
        co2ToSave: initialData.co2Saved ? String(initialData.co2Saved) : "",
        frequency: initialData.frequency || 1,
        repetition: initialData.repetition ? (initialData.repetition.charAt(0).toUpperCase() + initialData.repetition.slice(1).toLowerCase()) : "One-time",
        status: initialData.status ? (initialData.status.charAt(0).toUpperCase() + initialData.status.slice(1).toLowerCase()) : "Active",
        challengeImageUrl: initialData.challengeImageUrl || "",
        rewardBadgeImageUrl: initialData.rewardBadgeImageUrl || ""
      });

      setEffectiveFrom(initialData.effectiveDateFrom ? dayjs(initialData.effectiveDateFrom) : null);
      setEffectiveTo(initialData.effectiveDateTo ? dayjs(initialData.effectiveDateTo) : null);
      setChallengeImageFile(null);
      setBadgeImageFile(null);
      setChallengeImagePreview("");
      setBadgeImagePreview("");
      setErrors({});
      setTouched({});
    } else if (mode === 'create') {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (saved) {
        try {
          const draft = JSON.parse(saved);
          setFormData(draft.formData);
          setEffectiveFrom(draft.effectiveFrom ? dayjs(draft.effectiveFrom) : null);
          setEffectiveTo(draft.effectiveTo ? dayjs(draft.effectiveTo) : null);
          setHasDraft(true);
        } catch {
          // ignore corrupt draft
          setHasDraft(false);
        }
      } else {
        setFormData({
          name: "",
          type: "Monthly",
          mode: "Bicycle",
          keyRequirements: "",
          description: "",
          co2ToSave: "",
          frequency: 1,
          repetition: "One-time",
          status: "Active",
          challengeImageUrl: "",
          rewardBadgeImageUrl: ""
        });
        setEffectiveFrom(null);
        setEffectiveTo(null);
        setHasDraft(false);
      }
      setChallengeImageFile(null);
      setBadgeImageFile(null);
      setChallengeImagePreview("");
      setBadgeImagePreview("");
      setErrors({});
      setTouched({});
    }
  }, [open, mode, initialData]);

  // Persist draft while filling out the create form
  useEffect(() => {
    if (!open || mode !== 'create') return;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
      formData,
      effectiveFrom: effectiveFrom ? effectiveFrom.toISOString() : null,
      effectiveTo: effectiveTo ? effectiveTo.toISOString() : null,
    }));
    setHasDraft(true);
  }, [open, mode, formData, effectiveFrom, effectiveTo]);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleBlur = (field) => () => {
    setTouched({ ...touched, [field]: true });
    validateField(field, formData[field]);
  };

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'name':
        if (!value || value.trim() === '') {
          error = 'Challenge name is required';
        }
        break;
      case 'keyRequirements':
        if (!value || value.trim() === '') {
          error = 'Key requirements are required';
        }
        break;
      case 'description':
        if (!value || value.trim() === '') {
          error = 'Description is required';
        }
        break;
      case 'co2ToSave':
        if (!value || value.trim() === '') {
          error = 'CO2 to save is required';
        } else if (isNaN(value) || parseFloat(value) <= 0) {
          error = 'Must be a positive number';
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return error === '';
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // In edit mode, only validate fields that are editable (status, images)
    // All other fields are locked and don't need validation
    if (!isEditMode) {
      if (!formData.name || formData.name.trim() === '') {
        newErrors.name = 'Challenge name is required';
        isValid = false;
      }

      if (!formData.keyRequirements || formData.keyRequirements.trim() === '') {
        newErrors.keyRequirements = 'Key requirements are required';
        isValid = false;
      }

      if (!formData.description || formData.description.trim() === '') {
        newErrors.description = 'Description is required';
        isValid = false;
      }

      if (!formData.co2ToSave || formData.co2ToSave.trim() === '') {
        newErrors.co2ToSave = 'CO2 to save is required';
        isValid = false;
      } else if (isNaN(formData.co2ToSave) || parseFloat(formData.co2ToSave) <= 0) {
        newErrors.co2ToSave = 'Must be a positive number';
        isValid = false;
      }

    const now = dayjs();

    if (!effectiveFrom) {
      newErrors.effectiveFrom = 'Effective from date is required';
      isValid = false;
    } else if (effectiveFrom.isBefore(now)) {
      newErrors.effectiveFrom = 'Effective from date must be a future date';
      isValid = false;
    }

    if (!effectiveTo) {
      newErrors.effectiveTo = 'Effective to date is required';
      isValid = false;
    } else if (effectiveTo.isBefore(now)) {
      newErrors.effectiveTo = 'Effective to date must be a future date';
      isValid = false;
    }

      if (effectiveFrom && effectiveTo && effectiveTo.isBefore(effectiveFrom)) {
        newErrors.effectiveTo = 'End date must be after start date';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleFormSubmit = () => {
    // Validate form before submission
    if (!validateForm()) {
      message.error('Please fill in all required fields correctly');
      return;
    }

    let payload;

    if (isEditMode) {
      // In edit mode, only send editable fields: status, images
      payload = {
        status: formData.status.toUpperCase(),
        challengeImageUrl: formData.challengeImageUrl || challengeImagePreview,
        rewardBadgeImageUrl: formData.rewardBadgeImageUrl || badgeImagePreview,
      };
    } else {
      // Create mode: send full payload
      payload = {
        name: formData.name,
        type: formData.type.toUpperCase(),
        mode: formData.mode.toUpperCase(),
        keyRequirements: formData.keyRequirements,
        description: formData.description,
        frequency: parseInt(formData.frequency, 10),
        repetition: formData.repetition.toUpperCase().replace('-', '_'),
        status: formData.status.toUpperCase(),
        challengeImageUrl: formData.challengeImageUrl || challengeImagePreview,
        rewardBadgeImageUrl: formData.rewardBadgeImageUrl || badgeImagePreview,
        effectiveDateFrom: effectiveFrom ? effectiveFrom.toISOString() : null,
        effectiveDateTo: effectiveTo ? effectiveTo.toISOString() : null,
        co2Saved: parseFloat(formData.co2ToSave || 0)
      };
    }

    if (mode === 'create') { sessionStorage.removeItem(DRAFT_KEY); setHasDraft(false); }
    onSubmit(payload);
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  // Helper to get initial value safely
  const getVal = (field, defaultVal) => {
    if ((isViewMode || isEditMode) && initialData) {
      return initialData[field] || defaultVal;
    }
    return defaultVal;
  };

  const modalTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', paddingTop: '8px' }}>
      <ArrowLeftOutlined onClick={onClose} style={{ fontSize: '20px' }} />
      <span style={{ fontSize: '20px', fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>
        {isViewMode ? 'View Challenge' : isEditMode ? 'Update Challenge' : 'Create Challenge'}
      </span>
    </div>
  );

  const handleImageUpload = async (file, type) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }

    try {
      // Set uploading state
      if (type === 'challenge') {
        setUploadingChallenge(true);
      } else {
        setUploadingBadge(true);
      }

      // Generate unique key for S3
      const timestamp = Date.now();
      const fileName = file.name.replace(/\s+/g, '-');
      const s3Key = `challenges/${type}/${timestamp}-${fileName}`;

      // Get presigned URL
      console.log('Step 1: Requesting presigned URL for key:', s3Key, 'contentType:', file.type);
      const presignedResponse = await services.getS3PresignedUrl(s3Key, file.type);
      console.log('Step 2: Presigned URL response received:', presignedResponse);
      console.log('Step 2a: Response data:', presignedResponse.data);
      console.log('Step 2b: Response data type:', typeof presignedResponse.data);
      console.log('Step 2c: Full response structure:', JSON.stringify(presignedResponse.data, null, 2));

      // Try different possible response formats
      let presignedUrl = presignedResponse.data?.url ||
        presignedResponse.data?.data?.url ||
        (typeof presignedResponse.data === 'string' ? presignedResponse.data : null);

      console.log('Step 3: Extracted presigned URL:', presignedUrl);

      if (!presignedUrl) {
        console.error('❌ No presigned URL in response!');
        console.error('Response structure:', presignedResponse);
        alert('Failed to get presigned URL. Check console for details.');
        throw new Error('Failed to get presigned URL from server. Check response format.');
      }

      if (!presignedUrl.startsWith('http')) {
        console.error('❌ Invalid presigned URL format:', presignedUrl);
        alert('Invalid presigned URL received. Check console for details.');
        throw new Error('Invalid presigned URL format');
      }

      console.log('✅ Step 4: About to upload file to S3');
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      });
      console.log('Presigned URL (first 150 chars):', presignedUrl.substring(0, 150));

      // Upload to S3 using presigned URL
      // Create a new axios instance without interceptors to avoid Authorization header
      const s3Axios = axios.create();
      console.log('✅ Step 5: Created clean axios instance (no interceptors)');
      console.log('About to make PUT request to S3...');

      try {
        const uploadResponse = await s3Axios.put(presignedUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
          }
        });

        console.log('✅ Step 6: S3 upload successful!', uploadResponse.status);
        console.log('Upload response:', uploadResponse);
      } catch (uploadError) {
        console.error('❌ S3 Upload failed!');
        console.error('Upload error:', uploadError);
        console.error('Upload error message:', uploadError.message);
        console.error('Upload error response:', uploadError.response);
        alert(`S3 Upload failed: ${uploadError.message}`);
        throw uploadError;
      }

      // Extract the S3 URL (without query parameters)
      const s3Url = presignedUrl.split('?')[0];

      // Create preview for display
      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'challenge') {
          setChallengeImageFile(file);
          setChallengeImagePreview(e.target.result);
          setFormData({ ...formData, challengeImageUrl: s3Url });
          setUploadingChallenge(false);
          message.success('Challenge image uploaded successfully!');
        } else {
          setBadgeImageFile(file);
          setBadgeImagePreview(e.target.result);
          setFormData({ ...formData, rewardBadgeImageUrl: s3Url });
          setUploadingBadge(false);
          message.success('Badge image uploaded successfully!');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });

      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      message.error(`Failed to upload ${type} image: ${errorMessage}`);

      if (type === 'challenge') {
        setUploadingChallenge(false);
      } else {
        setUploadingBadge(false);
      }
    }

    return false; // Prevent default upload behavior
  };

  const UploadBox = ({ label, type }) => {
    const preview = type === 'challenge' ? challengeImagePreview : badgeImagePreview;
    const existingUrl = type === 'challenge' ? formData.challengeImageUrl : formData.rewardBadgeImageUrl;
    const displayImage = preview || existingUrl;
    const isUploading = type === 'challenge' ? uploadingChallenge : uploadingBadge;

    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#344054', fontFamily: 'Manrope, sans-serif' }}>
          {label}
        </Typography>
        <Upload
          beforeUpload={(file) => handleImageUpload(file, type)}
          showUploadList={false}
          accept="image/*"
          disabled={isViewMode}
        >
          <Box
            sx={{
              border: '1px dashed #D0D5DD',
              borderRadius: '8px',
              p: 3,
              textAlign: 'center',
              cursor: isViewMode ? 'default' : 'pointer',
              bgcolor: '#FFFFFF',
              '&:hover': { bgcolor: isViewMode ? '#FFFFFF' : '#F9FAFB' },
              position: 'relative'
            }}
          >
            {isUploading ? (
              <Box sx={{ py: 4 }}>
                <LoadingOutlined style={{ fontSize: '40px', color: '#5FB92A' }} />
                <Typography variant="body2" sx={{ mt: 2, fontFamily: 'Manrope, sans-serif', color: '#667085' }}>
                  Uploading to S3...
                </Typography>
              </Box>
            ) : displayImage ? (
              <Box sx={{ position: 'relative' }}>
                <img
                  src={displayImage}
                  alt={label}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '150px',
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
                {!isViewMode && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, fontFamily: 'Manrope, sans-serif' }}>
                    Click to change image
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#F2F4F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 1.5
                }}>
                  <CloudUploadOutlined style={{ fontSize: '20px', color: '#475467' }} />
                </Box>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600, fontFamily: 'Manrope, sans-serif', color: '#5FB92A' }}>
                  {isViewMode ? 'View Image' : 'Click to upload'}
                </Typography>
                {!isViewMode && (
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ fontFamily: 'Manrope, sans-serif' }}>
                    {' '}or drag and drop
                  </Typography>
                )}
                {!isViewMode && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'Manrope, sans-serif' }}>
                    SVG, PNG, JPG or GIF (max. 800×400px)
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Upload>
      </Box>
    );
  };


  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      title={modalTitle}
      closeIcon={null}
      className="create-challenge-modal"
      centered
      style={{
        maxHeight: 'calc(100vh - 40px)',
        paddingBottom: 0
      }}
      styles={{
        header: {
          borderBottom: 'none',
          marginBottom: 0,
          padding: '20px 24px 0 24px'
        },
        body: {
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 200px)',
          padding: '0 24px 0 24px'
        },
        content: {
          padding: 0,
          borderRadius: '16px',
          fontFamily: 'Manrope, sans-serif'
        },
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.45)'
        }
      }}
      destroyOnClose
      wrapClassName="modal-fullscreen-wrapper"
    >
      <ThemeProvider theme={muiTheme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box component="form" sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Row 1 */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Challenge Name"
                  variant="outlined"
                  value={formData.name}
                  onChange={handleChange('name')}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  InputLabelProps={{ shrink: true }}
                  disabled={isViewMode || isEditMode}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Type"
                  value={formData.type}
                  onChange={handleChange('type')}
                  variant="outlined"
                  disabled={isViewMode || isEditMode}
                >
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Daily">Daily</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Mode"
                  value={formData.mode}
                  onChange={handleChange('mode')}
                  variant="outlined"
                  disabled={isViewMode || isEditMode}
                >
                  <MenuItem value="Bicycle">Bicycle</MenuItem>
                  <MenuItem value="Walk">Walk</MenuItem>
                  <MenuItem value="Car">Car</MenuItem>
                  <MenuItem value="Scooter">Scooter</MenuItem>
                  <MenuItem value="Rideshare">Rideshare</MenuItem>
                  <MenuItem value="Train">Train</MenuItem>
                  <MenuItem value="Transit">Transit</MenuItem>
                  <MenuItem value="Any">Any</MenuItem>
                </TextField>
              </Grid>

              {/* Row 2: Key Requirements */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Key Requirements"
                  multiline
                  rows={4}
                  value={formData.keyRequirements}
                  onChange={handleChange('keyRequirements')}
                  error={Boolean(errors.keyRequirements)}
                  helperText={errors.keyRequirements}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  disabled={isViewMode || isEditMode}
                  required
                />
              </Grid>

              {/* Row 3: Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange('description')}
                  error={Boolean(errors.description)}
                  helperText={errors.description}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  disabled={isViewMode || isEditMode}
                  required
                />
              </Grid>

              {/* Row 4: Metrics */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Co2 to Save"
                  value={formData.co2ToSave}
                  onChange={handleChange('co2ToSave')}
                  error={Boolean(errors.co2ToSave)}
                  helperText={errors.co2ToSave}
                  variant="outlined"
                  disabled={isViewMode || isEditMode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Frequency"
                  type="number"
                  value={formData.frequency}
                  onChange={handleChange('frequency')}
                  variant="outlined"
                  disabled={isViewMode || isEditMode}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Repetition"
                  value={formData.repetition}
                  onChange={handleChange('repetition')}
                  variant="outlined"
                  disabled={isViewMode || isEditMode}
                >
                  <MenuItem value="One-time">One-time</MenuItem>
                  <MenuItem value="Recurring">Recurring</MenuItem>
                </TextField>
              </Grid>

              {/* Row 5: Status */}
              <Grid item xs={12}>
                <Box sx={{ maxWidth: { xs: '100%', md: '33.33%' } }}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={formData.status}
                    onChange={handleChange('status')}
                    variant="outlined"
                    disabled={isViewMode}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </TextField>
                </Box>
              </Grid>

              {/* Row 6: Images side-by-side */}
              <Grid item xs={12} md={6}>
                <UploadBox label="Challenge Image" type="challenge" />
              </Grid>
              <Grid item xs={12} md={6}>
                <UploadBox label="Reward Badge Image" type="badge" />
              </Grid>

              {/* Row 7: Dates */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: '#344054',
                  width: '183px',
                  height: '22px',
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  fontFamily: 'Manrope, sans-serif'
                }}>
                  Effective Date and Time
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label="Effective from"
                      value={effectiveFrom}
                      minDateTime={dayjs()}
                      open={effectiveFromOpen}
                      onOpen={() => setEffectiveFromOpen(true)}
                      onClose={() => setEffectiveFromOpen(false)}
                      onChange={(newValue) => {
                        setEffectiveFrom(newValue);
                        if (errors.effectiveFrom) {
                          setErrors({ ...errors, effectiveFrom: '' });
                        }
                      }}
                      inputFormat="D MMM YYYY hh:mm a"
                      disabled={isViewMode || isEditMode}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          disabled={isViewMode || isEditMode}
                          required
                          error={Boolean(errors.effectiveFrom)}
                          helperText={errors.effectiveFrom}
                          onClick={() => { if (!isViewMode && !isEditMode) setEffectiveFromOpen(true); }}
                          inputProps={{ ...params.inputProps, readOnly: true, placeholder: 'D MMM YYYY hh:mm am/pm' }}
                          sx={{
                            '& .MuiSvgIcon-root': { color: '#5FB92A' },
                            '& input': { cursor: 'pointer' }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateTimePicker
                      label="Effective to"
                      value={effectiveTo}
                      minDateTime={effectiveFrom || dayjs()}
                      open={effectiveToOpen}
                      onOpen={() => setEffectiveToOpen(true)}
                      onClose={() => setEffectiveToOpen(false)}
                      onChange={(newValue) => {
                        setEffectiveTo(newValue);
                        if (errors.effectiveTo) {
                          setErrors({ ...errors, effectiveTo: '' });
                        }
                      }}
                      inputFormat="D MMM YYYY hh:mm a"
                      disabled={isViewMode || isEditMode}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          disabled={isViewMode || isEditMode}
                          required
                          error={Boolean(errors.effectiveTo)}
                          helperText={errors.effectiveTo}
                          onClick={() => { if (!isViewMode && !isEditMode) setEffectiveToOpen(true); }}
                          inputProps={{ ...params.inputProps, readOnly: true, placeholder: 'D MMM YYYY hh:mm am/pm' }}
                          sx={{
                            '& .MuiSvgIcon-root': { color: '#5FB92A' },
                            '& input': { cursor: 'pointer' }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Footer Actions */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              mt: 4,
              pt: 2,
              pb: 3,
              borderTop: '1px solid #EAECF0',
              position: 'sticky',
              bottom: 0,
              bgcolor: '#FFFFFF',
              zIndex: 10
            }}>
              {!isViewMode && !isEditMode && hasDraft && (
                <Button
                  variant="text"
                  onClick={() => {
                    sessionStorage.removeItem(DRAFT_KEY);
                    setHasDraft(false);
                    setFormData({
                      name: "", type: "Monthly", mode: "Bicycle", keyRequirements: "",
                      description: "", co2ToSave: "", frequency: 1, repetition: "One-time",
                      status: "Active", challengeImageUrl: "", rewardBadgeImageUrl: ""
                    });
                    setEffectiveFrom(null);
                    setEffectiveTo(null);
                    setChallengeImageFile(null);
                    setBadgeImageFile(null);
                    setChallengeImagePreview("");
                    setBadgeImagePreview("");
                    setErrors({});
                    setTouched({});
                  }}
                  sx={{
                    color: '#F04438',
                    textTransform: 'none',
                    fontWeight: 600,
                    mr: 'auto',
                  }}
                >
                  Discard Draft
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  color: '#344054',
                  borderColor: '#D0D5DD',
                  borderWidth: '1px',
                  borderRadius: '8px',
                  height: '40px',
                  width: '82px',
                  padding: '10px 16px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#344054',
                    borderWidth: '1px'
                  }
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={isViewMode ? onEdit : handleFormSubmit}
                sx={{
                  bgcolor: '#5FB92A',
                  '&:hover': { bgcolor: '#4da61a' },
                  borderRadius: '8px',
                  height: '40px',
                  minWidth: '82px',
                  padding: '10px 16px',
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {isViewMode ? 'Edit details' : isEditMode ? 'Update' : 'Create Challenge'}
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      </ThemeProvider>
    </Modal>
  );
};

export default CreateChallengeModal;
